import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CardLinksResponse } from '../types/kanban';
import LinkIcon from './icons/LinkIcon';
import UnlinkIcon from './icons/UnlinkIcon';
import { UnlinkCardModal } from './UnlinkCardModal';

interface CardLinksProps {
  links: CardLinksResponse;
  boardId: number;
  onUnlink: (linkId: number) => void;
}

export function CardLinks({ links, boardId, onUnlink }: CardLinksProps) {
  const { parent_cards: parentCards, child_cards: childCards } = links;
  const hasLinks = parentCards.length > 0 || childCards.length > 0;

  const [pendingUnlink, setPendingUnlink] = useState<{
    id: number;
    cardName: string;
  } | null>(null);

  const handleConfirmUnlink = () => {
    if (pendingUnlink) {
      onUnlink(pendingUnlink.id);
      setPendingUnlink(null);
    }
  };

  if (!hasLinks) {
    return null;
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
        <span className="w-5 h-5">
          <LinkIcon />
        </span>
        Linked Cards
      </h2>

      {parentCards.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-400 mb-2 uppercase tracking-wide">
            Parent Cards
          </h3>
          <div className="space-y-2">
            {parentCards.map((link) => (
              <div
                key={link.id}
                className="flex items-center justify-between bg-gray-750 border border-gray-700 rounded-lg px-3 py-2 hover:bg-gray-700 transition-colors"
              >
                <Link
                  to={`/boards/${boardId}/cards/${link.card.id}`}
                  className="flex-1 text-gray-200 hover:text-blue-400 transition-colors truncate"
                >
                  <span className="text-gray-500 text-xs mr-2">
                    #{link.card.id_short || link.card.id}
                  </span>
                  {link.card.name}
                </Link>
                <button
                  onClick={() => onUnlink(link.id)}
                  className="text-gray-500 hover:text-red-400 transition-colors ml-2 p-1"
                  title="Unlink card"
                >
                  <span className="w-4 h-4">
                    <UnlinkIcon />
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {childCards.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-400 mb-2 uppercase tracking-wide">
            Child Cards
          </h3>
          <div className="space-y-2">
            {childCards.map((link) => (
              <div
                key={link.id}
                className="flex items-center justify-between bg-gray-750 border border-gray-700 rounded-lg px-3 py-2 hover:bg-gray-700 transition-colors"
              >
                <Link
                  to={`/boards/${boardId}/cards/${link.card.id}`}
                  className="flex-1 text-gray-200 hover:text-blue-400 transition-colors truncate"
                >
                  <span className="text-gray-500 text-xs mr-2">
                    #{link.card.id_short || link.card.id}
                  </span>
                  {link.card.name}
                </Link>
                <button
                  onClick={() =>
                    setPendingUnlink({
                      id: link.id,
                      cardName: link.card.name,
                    })
                  }
                  className="text-gray-500 hover:text-red-400 transition-colors ml-2 p-1"
                  title="Unlink card"
                >
                  <span className="w-4 h-4">
                    <UnlinkIcon />
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingUnlink && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <UnlinkCardModal
            cardName={pendingUnlink.cardName}
            onUnlink={handleConfirmUnlink}
            onClose={() => setPendingUnlink(null)}
          />
        </div>
      )}
    </div>
  );
}
